import React, { forwardRef } from 'react';
import classNames from 'classnames';

// Utils
import { isElementOfType } from '../../utilities/components';

// Components
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpButton from 'Wpds/components/button/WpButton';

// Styles
import './WpBadge.scss';

export type Color = 'blue' | 'green' | 'orange' | 'red' | 'yellow' | 'gray' | 'purple';
const DEFAULT_COLOR = 'gray';

export type BadgeVariant = 'default' | 'bold' | 'outline';
const DEFAULT_VARIANT = 'default';

type Size = 'sm' | 'md' | 'lg';
const DEFAULT_SIZE = 'md';

export interface Props {
  /** Color of the badge \n\n
   * Options: 'blue' | 'green' | 'orange' | 'red' | 'pink' | 'gray' | 'purple'
   * @default 'info'
   */
  color?: Color;
  /**
   * Size of the badge
   * Options: 'sm' | 'md' | 'lg'
   * @default 'md'
   */
  size?: Size;
  /**
   * Size of the badge
   * Options: 'default' | 'bold'
   * @default 'default'
   */
  variant?: BadgeVariant;
  /** The content to display inside the badge. */
  children?: string | React.ReactNode;
  onDismiss?: Function;
  className?: string;
}

const WpBadge = forwardRef(function (
  { color = DEFAULT_COLOR, size = DEFAULT_SIZE, variant = DEFAULT_VARIANT, children, onDismiss, className }: Props,
  ref: any
) {
  const classNamePrefix = 'WpBadge';
  const classes = classNames(
    classNamePrefix,
    `${classNamePrefix}--${color}`,
    `${classNamePrefix}--variant-${variant}`,
    `${classNamePrefix}--${size}`,
    className
  );

  return (
    <span className={classes} ref={ref}>
      {React.Children.map(children, (child: React.ReactElement<any>) => {
        if (child && isElementOfType(child, WpIcon)) {
          return <span className={`${classNamePrefix}__icon`}>{React.cloneElement(child)}</span>;
        }
        return child;
      })}
      {onDismiss && (
        <span className={`${classNamePrefix}__dismiss-btn`}>
          <WpButton onClick={() => onDismiss()} variant="tertiary" size="sm">
            <WpIcon name="times" size="sm" />
          </WpButton>
        </span>
      )}
    </span>
  );
});

export default WpBadge;
